import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogForm,
  DialogHeading,
  Link,
  RadioButton,
  RadioButtonGroup,
  Typography,
} from 'omakase';
import { Formik } from 'formik';
import { Theme, css } from '@emotion/react';
import { getGiftCardBalance } from 'selectors/patronSelectors';
import { gql } from '@apollo/client';
import { useActions } from '@tock/shared/utils/connect';
import { useDeleteWaitlistRequestsMutation } from 'generated/graphql';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useLogout } from 'hooks/useLogout';
import { useSelector } from 'react-redux';
import ConsumerDisplayMoney from 'components/ConsumerDisplayMoney';
import OrphanProtected from 'components/OrphanProtected';
import React from 'react';

const DIALOG_TITLE = 'delete-your-account-dialog-title';

const onDeleteTerms = [
  'Your account, gift card balance and any reservations associated with your account may not be recoverable.',
  'Your account and any personal information linked to your account will be deleted from Tock’s systems.',
];

const dialogContent = ({ breakpoints, spacing, typography }: Theme) => css`
  & > * + * {
    margin-top: ${spacing(4)}px;
  }
  & legend {
    ${typography.variant('body01Strong')} ${breakpoints.up('md')} {
      ${typography.variant('heading03')}
    }
  }
`;

const listStyle = ({ spacing }: Theme) => css`
  list-style: disc;
  line-height: 1.6;
  margin-left: ${spacing(2.5)}px;
`;

gql`
  mutation DeleteWaitlistRequests {
    deleteWaitlistRequests
  }
`;

export const DeleteAccountDialog = () => {
  const isTockGiftCardDeprecated = useFeatureFlag('GOODBYE_TOCK_GIFT_CARDS');
  const [open, setOpen] = React.useState(false);
  const giftCardBalance = useSelector(getGiftCardBalance);
  const { actions } = useActions();
  const [deleteWaitlistRequests] = useDeleteWaitlistRequestsMutation();

  const [doLogout] = useLogout();

  const openDialog = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleCancelAccount = (surveyResponse: string) => {
    actions.patron.fetchAchievements();
    return actions.app
      .cancelAccount({ surveyResponse })
      .then(() => {
        deleteWaitlistRequests();
      })
      .then(() => {
        doLogout({ onLogoutMessage: 'Your Tock account has been deleted.' });
      })
      .catch(() => {
        actions.toast.showError('Unable to delete account');
      });
  };

  return (
    <>
      <Typography variant="body02" mb={2}>
        Once you delete your account, you can’t undo it. Please be certain.
      </Typography>
      <Button variant="danger" size="large" onClick={openDialog}>
        Delete my account
      </Button>
      <Dialog aria-labelledby={DIALOG_TITLE} open={open} onClose={handleClose}>
        <Formik
          initialValues={{
            reason: 'NO_RESPONSE',
          }}
          onSubmit={(values) => {
            setOpen(false);
            handleCancelAccount(values.reason);
          }}
          validate={(values) => {
            if (values.reason === undefined) {
              return {
                reason: 'A reason must be selected.',
              };
            }
            return {};
          }}
        >
          <DialogForm>
            <DialogHeading id={DIALOG_TITLE} onClose={handleClose}>
              Delete your account
            </DialogHeading>
            <DialogContent css={dialogContent}>
              {!isTockGiftCardDeprecated &&
                giftCardBalance.data &&
                giftCardBalance.data.balanceCents > 0 && (
                  <Alert severity="error">
                    <p>
                      Warning: you have{' '}
                      <ConsumerDisplayMoney amount={giftCardBalance.data?.balanceCents ?? 0} /> Tock
                      gift card balance remaining. If you delete your account, you will lose your
                      gift card balance and it cannot be recovered.
                    </p>
                  </Alert>
                )}
              <div>
                <Typography variant="body01Strong" mb={2}>
                  Here’s what will happen if you delete your account:
                </Typography>
                <Typography variant="body02">
                  <ul>
                    {onDeleteTerms.map((term) => (
                      <Typography css={listStyle} variant="body02" component="li">
                        <OrphanProtected string={term} />
                      </Typography>
                    ))}
                  </ul>
                </Typography>
              </div>
              <div>
                <Typography variant="body02" mb={2}>
                  Additionally, please note that:
                </Typography>
                <Typography variant="body02">
                  <ul>
                    <Typography css={listStyle} variant="body02">
                      <li>
                        Deleting your account only deletes data which Tock controls. Restaurants or
                        other businesses with whom your account has made reservations or other
                        bookings via Tock may have their own data about you. With respect to any
                        such business-controlled data, you will need to reach out directly to each
                        such business.'
                      </li>
                    </Typography>
                    <Typography css={listStyle} variant="body02">
                      <li>
                        Our{' '}
                        <Link href="http://exploretock.com/privacy" target="_blank">
                          Privacy Policy
                        </Link>{' '}
                        includes additional information related to account deletion.
                      </li>
                    </Typography>
                    <li></li>
                  </ul>
                </Typography>
              </div>
              <RadioButtonGroup disclosureMode="static" name="reason" label="Why are you leaving?">
                <RadioButton
                  size="large"
                  weight="book"
                  value="USELESS"
                  label="I don't find it useful"
                />
                <RadioButton
                  size="large"
                  weight="book"
                  value="TEMPORARY"
                  label="It's temporary, I'll be back"
                />
                <RadioButton
                  size="large"
                  weight="book"
                  value="PRIVACY"
                  label="I have privacy concerns"
                />
                <RadioButton
                  size="large"
                  weight="book"
                  value="NO_RESPONSE"
                  label="Prefer not to say"
                />
              </RadioButtonGroup>
            </DialogContent>
            <DialogActions stack="mobile">
              <Button variant="basic" size="large" onClick={handleClose} type="button">
                I changed my mind
              </Button>
              <Button type="submit" variant="danger" size="large">
                Delete my account
              </Button>
            </DialogActions>
          </DialogForm>
        </Formik>
      </Dialog>
    </>
  );
};
